import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import "../supplier/supplierManagementTables.css";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import { KpiIcon } from "../../../DataManagment/Quantative/component/KpiIcons";
import AssessmentEmptyState from "./AssessmentEmptyState";
import {
  primaryBtn,
  secondaryBtn,
  tableCardSurface,
  thStyle,
} from "./assessmentStyles";

const ACTIVE = new Set(["ASSIGNED", "IN_PROGRESS"]);
const PAST = new Set(["SUBMITTED", "REVIEWED", "CANCELLED"]);

/**
 * Supplier-side starting page: every inviting company that has at least one
 * assignment for this supplier. Backend already scopes to supplier_id when
 * the logged-in user is type='supplier'; here we just group by company_id.
 */
const MyCompanies = () => {
  const history = useHistory();
  const location = useLocation();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await AssessmentService.listMyAssignments({ limit: 500 });
      setLoading(false);
      if (!res?.isSuccess) return;
      const items = res?.data?.data?.items ?? [];

      const byCompany = new Map();
      items.forEach((r) => {
        const id = r.company_id;
        if (!byCompany.has(id)) {
          byCompany.set(id, {
            companyId: id,
            companyName: r.company_name || `Company #${id}`,
            active: 0,
            past: 0,
          });
        }
        const g = byCompany.get(id);
        if (ACTIVE.has(r.status_key)) g.active += 1;
        if (PAST.has(r.status_key)) g.past += 1;
      });

      setGroups(
        Array.from(byCompany.values()).sort((a, b) =>
          a.companyName.localeCompare(b.companyName)
        )
      );
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const needle = search.trim().toLowerCase();
    return groups.filter((g) =>
      (g.companyName || "").toLowerCase().includes(needle)
    );
  }, [groups, search]);

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle="My Assessments"
      totalCount={filtered.length}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          padding: "12px 16px",
          background: "var(--surface)",
          border: "1px solid var(--rule)",
          borderRadius: 12,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1 1 220px",
            minWidth: 180,
            maxWidth: 320,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 11,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ink-4)",
              pointerEvents: "none",
            }}
          >
            <KpiIcon name="search" size={14} />
          </div>
          <input
            className="kpi-input"
            style={{ paddingLeft: 34, paddingRight: search ? 32 : 12 }}
            placeholder="Search company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search company"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 18,
                color: "var(--ink-4)",
                lineHeight: 1,
                padding: 0,
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
        <div className="supplier-mgmt-table-scroll">
          <table className="supplier-mgmt-table table">
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 50 }}>#</th>
                <th style={thStyle}>Company</th>
                <th style={{ ...thStyle, width: 110 }}>Active</th>
                <th style={{ ...thStyle, width: 110 }}>Past</th>
                <th
                  style={{ ...thStyle, width: 220, textAlign: "right" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <AssessmentEmptyState
                      title="No Companies Found"
                      message={
                        search
                          ? "Try adjusting the search term."
                          : "No assessments have been assigned to you yet."
                      }
                      hasFilters={Boolean(search)}
                      onClearFilters={() => setSearch("")}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((g, idx) => (
                  <tr key={g.companyId}>
                    <td>{idx + 1}</td>
                    <td>{g.companyName}</td>
                    <td>{g.active}</td>
                    <td>{g.past}</td>
                    <td style={{ textAlign: "right" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          gap: 8,
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            ...secondaryBtn,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 32,
                            padding: "0 14px",
                            fontSize: 12,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            width: "auto",
                          }}
                          onClick={() =>
                            history.push(
                              `/assessment/by-company/${g.companyId}/active`
                            )
                          }
                        >
                          Show Active
                        </button>
                        <button
                          type="button"
                          style={{
                            ...primaryBtn,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 32,
                            padding: "0 14px",
                            fontSize: 12,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            width: "auto",
                          }}
                          onClick={() =>
                            history.push(
                              `/assessment/by-company/${g.companyId}/past`
                            )
                          }
                        >
                          Show Past
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SupplierManagementShell>
  );
};

export default MyCompanies;
